/**
 * Class description
 *
 * Created: 2018-08-30
 * @author jonas
 */
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Ease, Power1, TimelineLite, TweenLite} from "gsap";
import {DirtyWildsLayerConfig} from "../../config/DirtyWildsLayerConfig";
import {DirtyWildsGameAssets} from "../DirtyWildsGameAssets";
import {DirtyWildsGameConfig} from "../DirtyWildsGameConfig";
import {DirtyWildGameMode} from "../features/DirtyWildsRespinZeroBetController";
import {EnvironmentSpine} from "./parts/EnvironmentSpine";
import {EnvironmentTintContainer} from "./parts/EnvironmentTintContainer";


export class DirtyWildsEnvironment extends BaseView {
    private _back:EnvironmentSpine;
    private _middle:EnvironmentSpine;
    private _front:EnvironmentSpine;
    private _water:EnvironmentSpine;
    private _temple:EnvironmentSpine;
    private _backDarkening:PIXI.spine.Spine;
    private _containerBack:PIXI.Container;
    private _containerMid:PIXI.Container;
    private _containerFront:PIXI.Container;

    private _allLayers:PIXI.Container[];
    private _zoomEasing:Ease = Power1.easeInOut;
    private _respinZoomMagnitude:number = 0.1;
    private _intensityZoomMagnitude:number = 0.2;
    private _layerFactors:number[];
    private _noWinContainers:EnvironmentTintContainer[];
    private _mysterySpine:EnvironmentSpine;
    private _brightLogo:PIXI.Sprite;
    private _mysterySync:PIXI.Sprite;
    private _mysteryWild:PIXI.Sprite;
    private _backMode:DirtyWildGameMode;

    private _isMysteryStopped:boolean;

    constructor() {
        super();
        this._noWinContainers = [];
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, () => this.onBeforeStart());
    }

    private onBeforeStart():void {
        this._isMysteryStopped = false;
    }

    protected initAnimations():void {
        PIXI.settings.PRECISION_FRAGMENT = PIXI.PRECISION.HIGH;
        let _this = this;
        const partSize:PIXI.Rectangle = new PIXI.Rectangle(1600, 1600);
        this._layerFactors = [1.0, 0.75, 0.5];
        this._water = new EnvironmentSpine(DirtyWildsGameAssets.ENV_WATER_SPINE, StageManager.getLayer(DirtyWildsLayerConfig.ENV_FRONT.name));
        this.createAllContainers();
        this._back = new EnvironmentSpine(DirtyWildsGameAssets.ENV_BACK_SPINE, this._containerBack);

        //This is the temple and the water, it should not scale at all.
        this._temple = new EnvironmentSpine(DirtyWildsGameAssets.ENV_TEMPLE_SPINE, StageManager.getLayer(DirtyWildsLayerConfig.ENV_MID.name));
        this._middle = new EnvironmentSpine(DirtyWildsGameAssets.ENV_MIDDLE_SPINE, this._containerFront);
        this._front = new EnvironmentSpine(DirtyWildsGameAssets.ENV_FRONT_SPINE, this._containerFront);
        this.createBackDarkening();
        this.createMysterSpine();
        this.createFeatureHighlight();
        this._allLayers = [
            this._containerBack,
            this._temple,
            this._containerMid,
            this._water,
            this._containerFront
        ];

        for(let layer of this._allLayers) {
            layer.position.set(partSize.width * 0.5 + DirtyWildsGameConfig.instance.REEL_AREA_WIDTH / 2, partSize.height * 0.5 + DirtyWildsGameConfig.instance.REEL_AREA_HEIGHT / 2);
            layer.pivot.set(partSize.width * 0.5, partSize.height * 0.5);
        }
        this._temple.position.y = this._temple.position.y + 9;
        this.startIdleAnimations();
    }

    private createFeatureHighlight():void {
        this._mysterySync = new PIXI.Sprite(GameResources.getTextures("MysteryFeature/MysteryWildText")[0]);
        this._mysterySync.alpha = 0;
        this._mysterySync.position.set(190, 190);
        this._mysteryWild = new PIXI.Sprite(GameResources.getTextures("MysteryFeature/MysteryFeatureText")[0]);
        this._mysteryWild.alpha = 0;
        this._mysteryWild.position.set(190, 210);
        StageManager.getLayer(DirtyWildsLayerConfig.NEAR_WIN_DIV_ATTENTIONS.name).addChild(this._mysterySync);
        StageManager.getLayer(DirtyWildsLayerConfig.NEAR_WIN_DIV_ATTENTIONS.name).addChild(this._mysteryWild);
    }

    public highlightFeature(featureName:string):TimelineLite {
        let featureSprite:PIXI.Sprite = featureName === "mysterySync" ? this._mysterySync : this._mysteryWild;
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(featureSprite, 0.7, {alpha : 1}));
        tl.add(new TweenLite(featureSprite, 0.7, {alpha : 0}), 0.9);
        return tl;
    }

    public startIdleAnimations() {
        this._back.startLoop();
        // this._middle.startLoop();
        this._water.startLoop();
    }

    private createAllContainers() {
        this._containerBack = new PIXI.Container();
        this._containerBack.name = "this._containerBack";
        StageManager.getLayer(DirtyWildsLayerConfig.ENV_BACK.name).addChild(this._containerBack);

        this._containerMid = new PIXI.Container();
        this._containerMid.name = "this._containerMid";
        StageManager.getLayer(DirtyWildsLayerConfig.ENV_MID.name).addChild(this._containerMid);

        this._containerFront = new PIXI.Container();
        this._containerFront.name = "this._containerFront";
        StageManager.getLayer(DirtyWildsLayerConfig.ENV_FRONT.name).addChild(this._containerFront);
    }

    private createBackDarkening():void {
        const partSize:PIXI.Rectangle = new PIXI.Rectangle(1600, 1600);
        this._backDarkening = new PIXI.spine.Spine(GameResources.getSpineAsset(DirtyWildsGameAssets.BACK_DARKENING_SPINE));
        StageManager.getLayer(DirtyWildsLayerConfig.ENV_FRONT.name).addChild(this._backDarkening);
        this._backDarkening.visible = false;
        this._backDarkening.position.set(partSize.width * 0.5 + DirtyWildsGameConfig.instance.REEL_AREA_WIDTH / 2, partSize.height * 0.5 + DirtyWildsGameConfig.instance.REEL_AREA_HEIGHT / 2);
        this._backDarkening.pivot.set(partSize.width * 0.5, partSize.height * 0.5);
    }

    public getTransition(mode:DirtyWildGameMode, duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(this._backMode != undefined && this._backMode === mode) {
            return tl;
        }
        this._backMode = mode;
        this._backDarkening.visible = true;
        if(mode == DirtyWildGameMode.STICKY_RESPIN) {
            tl.add(new SpineTween(this._backDarkening, 0, "enterDarkening"));
        } else {
            tl.add(new SpineTween(this._backDarkening, 0, "exitDarkening"));
        }
        return tl;
    }

    public getInitialWinTransition(isBigWin:boolean, animName:string):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(!isBigWin) {
            this._backDarkening.visible = true;
            tl.add(new SpineTween(this._backDarkening, 0, animName));
        }
        return tl;
    }

    public getDollyZoomIn(duration:number, intensity:number = 0):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const calculatedScale = this._respinZoomMagnitude + (this._intensityZoomMagnitude * intensity);

        tl.add([
            new TweenLite(this._containerBack.scale, duration, {
                x : 1 + calculatedScale * this._layerFactors[0],
                y : 1 + calculatedScale * this._layerFactors[0],
                ease : this._zoomEasing
            }),
            new TweenLite(this._containerMid.scale, duration, {
                x : 1 + calculatedScale * this._layerFactors[1],
                y : 1 + calculatedScale * this._layerFactors[1],
                ease : this._zoomEasing
            }),
            new TweenLite(this._containerFront.scale, duration, {
                x : 1 + calculatedScale * this._layerFactors[2],
                y : 1 + calculatedScale * this._layerFactors[2],
                ease : this._zoomEasing
            })
        ]);
        return tl;
    }

    public getDollyZoomOut(duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add([
            new TweenLite(this._containerBack.scale, duration, {x : 1.0, y : 1.0, ease : this._zoomEasing}),
            new TweenLite(this._containerMid.scale, duration, {x : 1.0, y : 1.0, ease : this._zoomEasing}),
            new TweenLite(this._containerFront.scale, duration, {x : 1.0, y : 1.0, ease : this._zoomEasing})
        ]);
        return tl;
    }

    private createMysterSpine():void {
        this._mysterySpine = new EnvironmentSpine(DirtyWildsGameAssets.MYSTERY_SPINE, StageManager.getLayer(DirtyWildsLayerConfig.NEAR_WIN_DIV_ATTENTIONS.name));
        this._brightLogo = new PIXI.Sprite(GameResources.getTextures("MysteryFeature/logo1")[0]);
        this._brightLogo.visible = false;
        this._brightLogo.position.set(308, 31);
        this._brightLogo.scale.set(0.32, 0.32);
        StageManager.getLayer(DirtyWildsLayerConfig.NEAR_WIN_DIV_ATTENTIONS.name).addChild(this._brightLogo);
    }

    public startMysteryPresentation(animName:string, loopCount:number, left:number, right:number, trackIndex:number = 1):SpineTween | null {
        if(this._isMysteryStopped) {
            return null;
        }
        this._mysterySpine.position.set(left, right);

        const frame:TimelineLite = new TimelineLite();
        frame.add(new SpineTween(this._mysterySpine.spine, trackIndex, animName, loopCount));
        if(animName === "FlashOnLogo") {
            frame.add(() => this._brightLogo.visible = true, frame.totalDuration() - 0.1);
        }
        return frame;
    }

    public stopMysteryPresentation():SpineTween {
        this._isMysteryStopped = true;
        const frame:TimelineLite = new TimelineLite();
        frame.add(() => {
            this._mysterySpine.spine.state.clearTrack(1);
            this._mysterySpine.spine.skeleton.setToSetupPose();
            this._brightLogo.visible = false;
        });
        return frame;
    }

    public addNoWinContainer(container:EnvironmentTintContainer) {
        this._noWinContainers.push(container);
    }

    public getHideNoWinColor(duration:number) {
        const tl:TimelineLite = new TimelineLite();
        for(let noWinCont of this._noWinContainers) {
            tl.add(noWinCont.getHideColorAnimation(duration), 0);
        }
        return tl;
    }


    public getShowNoWinColor(duration:number) {
        const tl:TimelineLite = new TimelineLite();
        for(let noWinCont of this._noWinContainers) {
            tl.add(noWinCont.getShowColorAnimation(duration), 0);
        }
        return tl;
    }
}